import React from "react";
import { FeatherChevronLeft, FeatherChevronRight } from "@subframe/core";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Loader } from "../../../../ui/components/Loader";
import { Table } from "../../../../ui/components/Table";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import { formatDate, truncateText } from "../../../../shared/utils/formatters";
import {
  getPostedStatusLabel,
  getStoredAccountingBackend,
} from "../../../../config/accounting";
import {
  CreditNoteEntity,
  CreditNoteListStatus,
  PaginationInfo,
} from "../../domain";

interface CreditNoteTableProps {
  creditNotes: CreditNoteEntity[];
  loading: boolean;
  inlineLoading?: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  itemsPerPage: number;
  searchTerm: string;
  onReview: (creditNote: CreditNoteEntity) => void;
  onPageChange: (page: number) => void;
}

export const CreditNoteTable: React.FC<CreditNoteTableProps> = ({
  creditNotes,
  loading,
  inlineLoading = false,
  error,
  pagination,
  itemsPerPage,
  searchTerm,
  onReview,
  onPageChange,
}) => {
  return (
    <div className="flex w-full flex-col items-start">
      <Table
        header={
          <Table.HeaderRow>
            <Table.HeaderCell className="w-40">
              Credit Note Number
            </Table.HeaderCell>
            <Table.HeaderCell className="w-64">Contact</Table.HeaderCell>
            <Table.HeaderCell className="w-32">Document Type</Table.HeaderCell>
            <Table.HeaderCell className="w-40">
              Document Number
            </Table.HeaderCell>
            <Table.HeaderCell className="w-40">Date</Table.HeaderCell>
            <Table.HeaderCell className="w-32">Amount</Table.HeaderCell>
            <Table.HeaderCell className="w-56">Status</Table.HeaderCell>
            <Table.HeaderCell className="w-32" />
          </Table.HeaderRow>
        }
      >
        {loading ? (
          <TableSkeletonRows rowCount={itemsPerPage} columnCount={8} />
        ) : inlineLoading ? (
          <Table.Row>
            <Table.Cell colSpan={8}>
              <div className="flex w-full items-center justify-center py-8">
                <Loader />
              </div>
            </Table.Cell>
          </Table.Row>
        ) : error ? (
          <Table.Row>
            <Table.Cell colSpan={8}>
              <div className="flex w-full items-center justify-center py-8">
                <span className="text-body font-body text-error-600">
                  {error}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : creditNotes.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={8}>
              <div className="flex w-full items-center justify-center py-8">
                <span className="text-body font-body text-neutral-500">
                  {searchTerm.trim()
                    ? "No credit notes match your search"
                    : "No credit notes found"}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : (
          creditNotes.map((creditNote) => (
            <CreditNoteTableRow
              key={creditNote.id}
              creditNote={creditNote}
              onReview={onReview}
            />
          ))
        )}
      </Table>

      {!loading && !inlineLoading && pagination && (
        <CreditNotePagination
          pagination={pagination}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

interface CreditNoteTableRowProps {
  creditNote: CreditNoteEntity;
  onReview: (creditNote: CreditNoteEntity) => void;
}

const CreditNoteTableRow: React.FC<CreditNoteTableRowProps> = ({
  creditNote,
  onReview,
}) => {
  const contactName = getDocumentName(creditNote);
  const documentType = getLinkedDocumentType(creditNote);

  return (
    <Table.Row>
      <Table.Cell className="w-40">
        <span className="text-body-bold font-body-bold text-default-font truncate">
          {truncateText(getDocumentNumber(creditNote), 20)}
        </span>
      </Table.Cell>
      <Table.Cell className="w-64">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar size="small" square={true}>
            {contactName.charAt(0).toUpperCase()}
          </Avatar>
          <span className="text-body font-body text-default-font truncate">
            {truncateText(contactName, 35)}
          </span>
        </div>
      </Table.Cell>
      <Table.Cell className="w-32">
        {documentType === "Invoice" ? (
          <Badge variant="success">Invoice</Badge>
        ) : documentType === "Bill" ? (
          <Badge variant="error">Bill</Badge>
        ) : (
          <span className="text-body font-body text-default-font">N/A</span>
        )}
      </Table.Cell>
      <Table.Cell className="w-40">
        <span className="text-body font-body text-default-font truncate">
          {truncateText(getLinkedDocumentNumber(creditNote), 20)}
        </span>
      </Table.Cell>
      <Table.Cell className="w-40">
        <span className="whitespace-nowrap text-body font-body text-neutral-500">
          {formatDate(getDocumentDate(creditNote), "medium")}
        </span>
      </Table.Cell>
      <Table.Cell className="w-32">
        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
          {getDocumentAmount(creditNote)}
        </span>
      </Table.Cell>
      <Table.Cell className="w-56">
        <div className="flex flex-col items-start gap-1">
          <CreditNoteStatusChip creditNote={creditNote} />
          {creditNote.updated_at && (
            <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
              {getStatusDateLabel(creditNote)}
            </span>
          )}
        </div>
      </Table.Cell>
      <Table.Cell className="w-32">
        <div className="flex grow shrink-0 basis-0 items-center justify-center">
          <Button
            variant="neutral-primary"
            onClick={() => onReview(creditNote)}
          >
            Review
          </Button>
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

interface CreditNotePaginationProps {
  pagination: PaginationInfo;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const CreditNotePagination: React.FC<CreditNotePaginationProps> = ({
  pagination,
  itemsPerPage,
  onPageChange,
}) => {
  const currentPage = pagination.current_page || 1;
  const totalPages = pagination.total_pages || 0;

  if (totalPages <= 1) return null;

  return (
    <div className="flex w-full items-center justify-between mt-4 flex-shrink-0">
      <span className="text-body font-body text-subtext-color">
        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
        {Math.min(currentPage * itemsPerPage, pagination.total)} of{" "}
        {pagination.total} results
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          icon={<FeatherChevronLeft />}
        >
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {getVisiblePages(currentPage, totalPages).map((pageNumber) => (
            <Button
              key={pageNumber}
              variant={
                currentPage === pageNumber
                  ? "brand-primary"
                  : "neutral-tertiary"
              }
              size="small"
              onClick={() => onPageChange(pageNumber)}
              className="min-w-[32px]"
            >
              {pageNumber}
            </Button>
          ))}
        </div>
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!pagination.has_more}
          icon={<FeatherChevronRight />}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const getVisiblePages = (currentPage: number, totalPages: number) => {
  return Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
    if (totalPages <= 5) return index + 1;
    if (currentPage <= 3) return index + 1;
    if (currentPage >= totalPages - 2) return totalPages - 4 + index;
    return currentPage - 2 + index;
  });
};

const getDocumentAmount = (creditNote: CreditNoteEntity): string => {
  const currencySymbol = creditNote.json?.currency_symbol || "";
  const total = creditNote.json?.total;
  const numericTotal = Number(total);

  if (Number.isFinite(numericTotal)) {
    return `${currencySymbol}${numericTotal.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  return "N/A";
};

const getDocumentName = (creditNote: CreditNoteEntity): string => {
  return (
    creditNote.json?.vendor_name ||
    creditNote.json?.customer_name ||
    "Unknown Contact"
  );
};

const getDocumentNumber = (creditNote: CreditNoteEntity): string => {
  return (
    creditNote.document_number ||
    creditNote.json?.creditnote_number ||
    creditNote.id.toString()
  );
};

const getLinkedDocumentType = (creditNote: CreditNoteEntity): string => {
  if (creditNote.json?.vendor_id) return "Bill";
  if (creditNote.json?.customer_id) return "Invoice";
  return "N/A";
};

const getLinkedDocumentNumber = (creditNote: CreditNoteEntity): string => {
  return creditNote.json?.reference_number || "N/A";
};

const getDocumentDate = (creditNote: CreditNoteEntity): string => {
  return creditNote.json?.date || creditNote.created_at || "";
};

const getCreditNoteListStatus = (
  creditNote: CreditNoteEntity,
): CreditNoteListStatus => {
  if (String(creditNote.is_under_review ?? "0") === "1") return "under_review";
  if (creditNote.status === "1") return "posted";
  if (creditNote.status === "2") return "rejected";
  return "pending";
};

const CreditNoteStatusChip: React.FC<{ creditNote: CreditNoteEntity }> = ({
  creditNote,
}) => {
  const status = getCreditNoteListStatus(creditNote);

  switch (status) {
    case "posted":
      return (
        <Badge variant="success">
          {getPostedStatusLabel(getStoredAccountingBackend())}
        </Badge>
      );
    case "under_review":
      return <Badge variant="warning">Under Review</Badge>;
    case "rejected":
      return <Badge variant="error">Rejected</Badge>;
    case "pending":
    default:
      return <Badge variant="warning">Pending</Badge>;
  }
};

const getStatusDateLabel = (creditNote: CreditNoteEntity): string => {
  const status = getCreditNoteListStatus(creditNote);
  const formattedDate = formatDate(creditNote.updated_at || "", "medium");

  if (status === "rejected") return `Rejected at ${formattedDate}`;
  return `Modified ${formattedDate}`;
};
